import { getCentralDbConfig } from './config';

export type CentralJsonResult<T> = {
  ok: boolean;
  data?: T;
  /** Server mengembalikan HTML (biasanya index.html dari Nginx), bukan API JSON. */
  notJson?: boolean;
};

async function readCentralJson<T>(response: Response): Promise<CentralJsonResult<T>> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return { ok: false, notJson: true };
  }
  try {
    const data = await response.json() as T;
    return { ok: true, data };
  } catch {
    return { ok: false };
  }
}

export async function centralFetch<T>(
  path: string,
  options: RequestInit & { method?: string } = {},
): Promise<T | undefined> {
  const config = getCentralDbConfig();
  if (!config) return undefined;

  const url = `${config.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-RasiTech-Key': config.apiKey,
        ...(options.headers as Record<string, string> | undefined),
      },
      signal: options.signal ?? abortAfterMs(15000),
    });
    if (!response.ok) {
      return undefined;
    }
    if (response.status === 204) return undefined as T;
    const parsed = await readCentralJson<T>(response);
    if (!parsed.ok || parsed.notJson) {
      return undefined;
    }
    return parsed.data;
  } catch {
    return undefined;
  }
}

let centralPostFailureStreak = 0;
let centralPostPausedUntil = 0;

export function isCentralPostPaused(): boolean {
  return Date.now() < centralPostPausedUntil;
}

function noteCentralPostFailure(response: Response, parsed: CentralJsonResult<unknown>) {
  if (response.status === 405 || parsed.notJson) {
    centralPostFailureStreak += 1;
    if (centralPostFailureStreak >= 3) {
      centralPostPausedUntil = Date.now() + 120_000;
    }
    return;
  }
  centralPostFailureStreak += 1;
}

export async function centralPost(path: string, body: unknown): Promise<boolean> {
  const config = getCentralDbConfig();
  if (!config) return false;
  if (isCentralPostPaused()) return false;

  const url = `${config.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-RasiTech-Key': config.apiKey,
      },
      body: JSON.stringify(body),
      signal: abortAfterMs(20000),
    });
    const parsed = await readCentralJson<{ ok?: boolean }>(response);
    if (!response.ok) {
      noteCentralPostFailure(response, parsed);
      return false;
    }
    if (!parsed.ok || parsed.notJson) {
      noteCentralPostFailure(response, parsed);
      return false;
    }
    centralPostFailureStreak = 0;
    return parsed.data?.ok !== false;
  } catch {
    centralPostFailureStreak += 1;
    if (centralPostFailureStreak >= 3) {
      centralPostPausedUntil = Date.now() + 120_000;
    }
    return false;
  }
}

export async function centralPut(path: string, body: unknown): Promise<boolean> {
  const config = getCentralDbConfig();
  if (!config) return false;

  const url = `${config.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-RasiTech-Key': config.apiKey,
      },
      body: JSON.stringify(body),
      signal: abortAfterMs(20000),
    });
    if (!response.ok) return false;
    const parsed = await readCentralJson<{ ok?: boolean }>(response);
    return parsed.ok && Boolean(parsed.data?.ok);
  } catch {
    return false;
  }
}

function abortAfterMs(ms: number): AbortSignal | undefined {
  if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) {
    return AbortSignal.timeout(ms);
  }
  return undefined;
}
